import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft, Save, Bell, Calendar, CalendarRange, Zap, Mail,
  Send, Eye, AlertTriangle, Clock, CheckCircle2, XCircle, RefreshCw, Server,
  MessageSquare, Database, Trophy,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReportSettingsPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };

  const [settings, setSettings] = useState(null);
  const [scheduler, setScheduler] = useState(null);
  const [providerStatus, setProviderStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [previewType, setPreviewType] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [smsDiag, setSmsDiag] = useState(null);
  const [valkeyStatus, setValkeyStatus] = useState(null);
  const [mongoStatus, setMongoStatus] = useState(null);
  const [dealAlertDiag, setDealAlertDiag] = useState(null);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [sRes, schRes, pRes, smsRes, vkRes, mgRes, daRes] = await Promise.all([
          axios.get(`${API}/settings/reports`, { headers }),
          axios.get(`${API}/reports/scheduler/status`, { headers }),
          axios.get(`${API}/email/provider/status`, { headers }),
          axios.get(`${API}/sms/diagnostics`, { headers }),
          axios.get(`${API}/system/valkey/status`, { headers }),
          axios.get(`${API}/system/mongodb/status`, { headers }),
          axios.get(`${API}/deal-scores/alerts/diagnostics`, { headers }).catch(() => ({ data: null })),
        ]);
        setSettings(sRes.data);
        setScheduler(schRes.data);
        setProviderStatus(pRes.data);
        setSmsDiag(smsRes.data);
        setValkeyStatus(vkRes.data);
        setMongoStatus(mgRes.data);
        setDealAlertDiag(daRes.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [token]);

  const update = (key, value) => { setSettings(prev => ({ ...prev, [key]: value })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        hot_lead_alerts: settings.hot_lead_alerts,
        seller_lead_alerts: settings.seller_lead_alerts,
        callback_alerts: settings.callback_alerts,
        failed_transfer_alerts: settings.failed_transfer_alerts,
        daily_report: settings.daily_report,
        weekly_report: settings.weekly_report,
        weekly_impact_email: settings.weekly_impact_email,
        followup_automation: settings.followup_automation,
        deal_alerts: settings.deal_alerts,
        report_email: settings.report_email || null,
      };
      const { data } = await axios.put(`${API}/settings/reports`, payload, { headers });
      setSettings(data);
      setDirty(false);
      toast.success('Settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const sendTestEmail = async () => {
    setSendingTest(true);
    try {
      const { data } = await axios.post(`${API}/reports/test-email`, {}, { headers });
      toast.success(`Test email sent to ${data.sent_to} (${data.delivery.provider})`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  const loadPreview = useCallback(async (type) => {
    if (previewType === type && previewData) { setPreviewType(null); setPreviewData(null); return; }
    setPreviewType(type);
    setPreviewLoading(true);
    try {
      const url = type === 'impact' ? `${API}/weekly-email/preview` : `${API}/reports/preview/${type}`;
      const { data } = await axios.get(url, { headers });
      setPreviewData(data);
    } catch { toast.error('Preview failed'); setPreviewType(null); }
    finally { setPreviewLoading(false); }
  }, [previewType, previewData, headers]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto space-y-5 sm:space-y-6" data-testid="report-settings-page">
      <div className="flex items-center gap-3">
        <Button size="sm" variant="ghost" onClick={() => navigate('/reports')} className="text-muted-foreground hover:text-foreground h-8 w-8 p-0" data-testid="back-to-reports">
          <ArrowLeft size={16} />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-foreground">Report Settings</h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">Configure reports, follow-ups, and email delivery</p>
        </div>
        <Button size="sm" onClick={save} disabled={!dirty || saving} className="bg-emerald-600 hover:bg-emerald-700 text-foreground gap-2" data-testid="save-settings-btn">
          <Save size={14} /> {saving ? 'Saving...' : 'Save'}
        </Button>
      </div>

      {/* Email Provider Status */}
      <Card className="border-border bg-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-foreground text-base flex items-center gap-2">
            <Server size={16} className="text-cyan-400" /> Email Provider
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {providerStatus && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatusChip label="Provider" value={providerStatus.provider?.toUpperCase()} color={providerStatus.provider === 'ses' ? 'emerald' : 'blue'} />
              <StatusChip label="SES Configured" value={providerStatus.ses_configured ? 'Yes' : 'No'} color={providerStatus.ses_configured ? 'emerald' : 'slate'} />
              <StatusChip label="Sender Verified" value={providerStatus.sender_verified ? 'Yes' : 'No'} color={providerStatus.sender_verified ? 'emerald' : 'slate'} />
              <StatusChip label="Service" value={providerStatus.email_service_enabled ? 'Enabled' : 'Disabled'} color={providerStatus.email_service_enabled ? 'emerald' : 'red'} />
            </div>
          )}
          {providerStatus?.ses_error && (
            <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-500/5 border border-amber-500/10 text-xs">
              <AlertTriangle size={12} className="text-amber-400 mt-0.5 shrink-0" />
              <span className="text-amber-300">{providerStatus.ses_error}</span>
            </div>
          )}
          <div className="flex items-center gap-2 pt-1">
            <Button size="sm" onClick={sendTestEmail} disabled={sendingTest} className="bg-blue-600 hover:bg-blue-700 text-foreground gap-2 text-xs h-8" data-testid="send-test-email-btn">
              {sendingTest ? <RefreshCw size={12} className="animate-spin" /> : <Send size={12} />}
              Send Test Email to Me
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Scheduler Status */}
      <Card className="border-border bg-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-foreground text-base flex items-center gap-2">
            <Clock size={16} className="text-indigo-400" /> Background Scheduler
          </CardTitle>
        </CardHeader>
        <CardContent>
          {scheduler && (
            <div className="space-y-2.5">
              <div className="flex items-center gap-2">
                {scheduler.running ? <CheckCircle2 size={14} className="text-emerald-400" /> : <XCircle size={14} className="text-red-400" />}
                <span className="text-sm text-foreground">{scheduler.running ? 'Scheduler running' : 'Scheduler stopped'}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {scheduler.jobs?.map(job => (
                  <div key={job.id} className="bg-muted rounded-lg p-3">
                    <p className="text-xs text-muted-foreground">{job.name}</p>
                    <p className="text-sm text-foreground mt-0.5">
                      Next: {job.next_run ? new Date(job.next_run).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }) : 'N/A'}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Email Preview */}
      <Card className="border-border bg-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-foreground text-base flex items-center gap-2">
            <Eye size={16} className="text-violet-400" /> Email Preview
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-2">
            <Button size="sm" variant={previewType === 'daily' ? 'default' : 'outline'} onClick={() => loadPreview('daily')} disabled={previewLoading}
              className={`gap-2 text-xs h-8 ${previewType === 'daily' ? 'bg-emerald-600 hover:bg-emerald-700 text-foreground' : 'border-border text-foreground'}`} data-testid="preview-daily-btn">
              {previewLoading && previewType === 'daily' ? <RefreshCw size={12} className="animate-spin" /> : <Calendar size={12} />}
              {previewType === 'daily' ? 'Hide Daily Preview' : 'Preview Daily Digest'}
            </Button>
            <Button size="sm" variant={previewType === 'weekly' ? 'default' : 'outline'} onClick={() => loadPreview('weekly')} disabled={previewLoading}
              className={`gap-2 text-xs h-8 ${previewType === 'weekly' ? 'bg-blue-600 hover:bg-blue-700 text-foreground' : 'border-border text-foreground'}`} data-testid="preview-weekly-btn">
              {previewLoading && previewType === 'weekly' ? <RefreshCw size={12} className="animate-spin" /> : <CalendarRange size={12} />}
              {previewType === 'weekly' ? 'Hide Weekly Preview' : 'Preview Weekly Digest'}
            </Button>
            <Button size="sm" variant={previewType === 'impact' ? 'default' : 'outline'} onClick={() => loadPreview('impact')} disabled={previewLoading}
              className={`gap-2 text-xs h-8 ${previewType === 'impact' ? 'bg-violet-600 hover:bg-violet-700 text-foreground' : 'border-border text-foreground'}`} data-testid="preview-impact-btn">
              {previewLoading && previewType === 'impact' ? <RefreshCw size={12} className="animate-spin" /> : <Zap size={12} />}
              {previewType === 'impact' ? 'Hide Impact Preview' : 'Preview Impact Email'}
            </Button>
          </div>
          {previewType && previewData && (
            <div className="mt-3" data-testid="email-preview-panel">
              <div className="flex items-center gap-2 mb-2">
                <Badge className="text-[10px] bg-violet-500/10 text-violet-400 border-violet-500/20">{previewType} preview</Badge>
                <span className="text-xs text-muted-foreground truncate">{previewData.subject}</span>
              </div>
              <div className="bg-background rounded-lg border border-border p-1 max-h-[400px] overflow-auto">
                <iframe srcDoc={previewData.html_content} title="Email Preview" className="w-full min-h-[300px] border-0 rounded" sandbox="" data-testid="email-preview-iframe" />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Report Email */}
      <Card className="border-border bg-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-foreground text-base flex items-center gap-2">
            <Mail size={16} className="text-blue-400" /> Delivery Address
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-sm text-muted-foreground">Report email address</Label>
            <Input type="email" placeholder="agent@example.com" value={settings?.report_email || ''} onChange={e => update('report_email', e.target.value)}
              className="mt-1.5 bg-background border-border text-foreground placeholder:text-muted-foreground" data-testid="report-email-input" />
            <p className="text-xs text-muted-foreground mt-1">Leave empty to use your account email</p>
          </div>
        </CardContent>
      </Card>

      {/* Digest Settings */}
      <Card className="border-border bg-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-foreground text-base flex items-center gap-2">
            <Calendar size={16} className="text-emerald-400" /> Scheduled Digests
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <ToggleRow id="daily_report" icon={<Calendar size={14} className="text-emerald-400" />} label="Daily Digest" description="Summary of calls, leads, and insights every morning (7am UTC)" checked={settings?.daily_report ?? true} onChange={v => update('daily_report', v)} />
          <ToggleRow id="weekly_report" icon={<CalendarRange size={14} className="text-blue-400" />} label="Weekly Digest" description="Comprehensive weekly performance summary (Monday 7am UTC)" checked={settings?.weekly_report ?? true} onChange={v => update('weekly_report', v)} />
          <ToggleRow id="weekly_impact_email" icon={<Zap size={14} className="text-violet-400" />} label="Weekly Impact Email" description="Personalized ROI summary with coaching insights (Monday 7:30am UTC)" checked={settings?.weekly_impact_email ?? true} onChange={v => update('weekly_impact_email', v)} />
          <ToggleRow id="deal_alerts" icon={<Trophy size={14} className="text-amber-400" />} label="Deal Alert Digest" description="Daily email with newly scored strong & exceptional investment opportunities" checked={settings?.deal_alerts ?? true} onChange={v => update('deal_alerts', v)} />
        </CardContent>
      </Card>

      {/* Alert Settings */}
      <Card className="border-border bg-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-foreground text-base flex items-center gap-2">
            <Bell size={16} className="text-amber-400" /> Instant Alerts
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <ToggleRow id="hot_lead_alerts" icon={<Zap size={14} className="text-red-400" />} label="Hot Lead Alerts" description="Immediate notification for high-priority leads" checked={settings?.hot_lead_alerts ?? true} onChange={v => update('hot_lead_alerts', v)} />
          <ToggleRow id="seller_lead_alerts" icon={<Zap size={14} className="text-amber-400" />} label="Seller Lead Alerts" description="Alert when a potential seller contacts your assistant" checked={settings?.seller_lead_alerts ?? true} onChange={v => update('seller_lead_alerts', v)} />
          <ToggleRow id="callback_alerts" icon={<Bell size={14} className="text-cyan-400" />} label="Callback Alerts" description="Notify when a caller requests a callback or appointment" checked={settings?.callback_alerts ?? true} onChange={v => update('callback_alerts', v)} />
          <ToggleRow id="failed_transfer_alerts" icon={<AlertTriangle size={14} className="text-orange-400" />} label="Failed Transfer Alerts" description="Alert when a call transfer attempt fails" checked={settings?.failed_transfer_alerts ?? true} onChange={v => update('failed_transfer_alerts', v)} />
        </CardContent>
      </Card>

      {/* Follow-up Automation */}
      <Card className="border-border bg-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-foreground text-base flex items-center gap-2">
            <Send size={16} className="text-indigo-400" /> Follow-up Automation
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ToggleRow id="followup_automation" icon={<Send size={14} className="text-indigo-400" />} label="Enable Automated Follow-ups" description="Auto-send follow-up emails/SMS after calls based on rules" checked={settings?.followup_automation ?? true} onChange={v => update('followup_automation', v)} />
        </CardContent>
      </Card>

      {/* Deal Alert Diagnostics */}
      <Card className="border-border bg-card" data-testid="deal-alert-diagnostics-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-foreground text-base flex items-center gap-2">
            <Trophy size={16} className="text-amber-400" /> Deal Alert Digest
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {dealAlertDiag ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <StatusChip label="Strong Deals (24h)" value={String(dealAlertDiag.current_strong_deals_24h ?? 0)} color={dealAlertDiag.current_strong_deals_24h > 0 ? 'emerald' : 'slate'} />
                <StatusChip label="Last Run" value={dealAlertDiag.last_run ? new Date(dealAlertDiag.last_run.run_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'Never'} color={dealAlertDiag.last_run ? 'blue' : 'slate'} />
                <StatusChip label="Last Sent" value={dealAlertDiag.last_run ? String(dealAlertDiag.last_run.sent ?? 0) : '0'} color={dealAlertDiag.last_run?.sent > 0 ? 'emerald' : 'slate'} />
              </div>
              {dealAlertDiag.deals_preview?.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Current Top Deals</p>
                  <div className="space-y-1.5">
                    {dealAlertDiag.deals_preview.map((d, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-muted rounded-lg text-xs">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className={`font-bold ${d.level === 'exceptional' ? 'text-amber-400' : 'text-emerald-400'}`}>{d.score}/100</span>
                          <Badge className={`text-[9px] ${d.level === 'exceptional' ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'}`}>{d.level}</Badge>
                        </div>
                        <span className="text-muted-foreground truncate ml-2">{d.reasons?.[0]?.slice(0, 40) || d.property_id?.slice(0, 8)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {dealAlertDiag.last_run?.errors > 0 && (
                <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-500/5 border border-amber-500/10 text-xs">
                  <AlertTriangle size={12} className="text-amber-400 mt-0.5 shrink-0" />
                  <span className="text-amber-300">Last run had {dealAlertDiag.last_run.errors} error(s)</span>
                </div>
              )}
            </>
          ) : (
            <p className="text-xs text-muted-foreground text-center py-4">Deal alert diagnostics unavailable</p>
          )}
        </CardContent>
      </Card>

      {/* SMS Diagnostics */}
      <Card className="border-border bg-card" data-testid="sms-diagnostics-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-foreground text-base flex items-center gap-2">
            <MessageSquare size={16} className="text-teal-400" /> SMS Provider
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {smsDiag && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatusChip label="Provider" value={smsDiag.provider?.toUpperCase()} color="blue" />
                <StatusChip label="Configured" value={smsDiag.configured ? 'Yes' : 'No'} color={smsDiag.configured ? 'emerald' : 'slate'} />
                <StatusChip label="Live Send" value={smsDiag.sms_enabled ? 'Enabled' : 'Disabled'} color={smsDiag.sms_enabled ? 'emerald' : 'red'} />
                <StatusChip label="Opted-Out" value={String(smsDiag.opted_out_count)} color="slate" />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatusChip label="Total Sent" value={String(smsDiag.stats?.sent || 0)} color="emerald" />
                <StatusChip label="Queued" value={String(smsDiag.stats?.queued || 0)} color="blue" />
                <StatusChip label="Failed" value={String(smsDiag.stats?.failed || 0)} color={smsDiag.stats?.failed > 0 ? 'red' : 'slate'} />
                <StatusChip label="Suppressed" value={String(smsDiag.stats?.suppressed || 0)} color="slate" />
              </div>
              {smsDiag.last_send && (
                <div className="bg-muted rounded-lg p-2.5 text-xs space-y-1">
                  <p className="text-muted-foreground">Last send</p>
                  <p className="text-foreground">Status: <span className={smsDiag.last_send.status === 'sent' || smsDiag.last_send.status === 'simulated' ? 'text-emerald-400' : 'text-red-400'}>{smsDiag.last_send.status}</span> to {smsDiag.last_send.phone}</p>
                  {smsDiag.last_send.error && <p className="text-red-400">{smsDiag.last_send.error}</p>}
                </div>
              )}
              {!smsDiag.sms_enabled && (
                <div className="flex items-start gap-2 p-2.5 rounded-lg bg-blue-500/5 border border-blue-500/10 text-xs">
                  <AlertTriangle size={12} className="text-blue-400 mt-0.5 shrink-0" />
                  <span className="text-blue-300">SMS sending is in simulation mode. Set SMS_ENABLE_SEND=true to enable live delivery.</span>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* MongoDB Status */}
      <Card className="border-border bg-card" data-testid="mongodb-status-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-foreground text-base flex items-center gap-2">
            <Database size={16} className="text-emerald-400" /> MongoDB
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {mongoStatus && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatusChip label="Status" value={mongoStatus.connected ? 'Connected' : 'Disconnected'} color={mongoStatus.connected ? 'emerald' : 'red'} />
                <StatusChip label="Database" value={mongoStatus.database_name?.toUpperCase()} color="blue" />
                <StatusChip label="Version" value={mongoStatus.version || 'N/A'} color="slate" />
                <StatusChip label="Collections" value={String(mongoStatus.collections?.length || 0)} color="emerald" />
              </div>
              {mongoStatus.host && (
                <div className="bg-muted rounded-lg p-2.5 text-xs">
                  <p className="text-muted-foreground">Host ({mongoStatus.source})</p>
                  <p className="text-foreground font-mono text-[11px] mt-0.5 break-all">{mongoStatus.host}</p>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Valkey Status */}
      <Card className="border-border bg-card" data-testid="valkey-status-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-foreground text-base flex items-center gap-2">
            <Database size={16} className="text-violet-400" /> Valkey (Cache/Queue)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {valkeyStatus && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatusChip label="Status" value={valkeyStatus.connected ? 'Connected' : 'Disconnected'} color={valkeyStatus.connected ? 'emerald' : 'red'} />
                <StatusChip label="TLS" value={valkeyStatus.tls ? 'Enabled' : 'Disabled'} color={valkeyStatus.tls ? 'emerald' : 'slate'} />
                <StatusChip label="Prefix" value={valkeyStatus.prefix?.toUpperCase()} color="blue" />
                {valkeyStatus.redis_version && <StatusChip label="Version" value={valkeyStatus.redis_version} color="slate" />}
              </div>
              {valkeyStatus.host && (
                <div className="bg-muted rounded-lg p-2.5 text-xs">
                  <p className="text-muted-foreground">Host</p>
                  <p className="text-foreground font-mono text-[11px] mt-0.5 break-all">{valkeyStatus.host}</p>
                </div>
              )}
              {!valkeyStatus.connected && (
                <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-500/5 border border-amber-500/10 text-xs">
                  <AlertTriangle size={12} className="text-amber-400 mt-0.5 shrink-0" />
                  <span className="text-amber-300">Valkey not reachable. Queue, lock, and cache features will gracefully degrade to MongoDB.</span>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatusChip({ label, value, color }) {
  const c = { emerald: 'text-emerald-400', blue: 'text-blue-400', slate: 'text-muted-foreground', red: 'text-red-400' }[color] || 'text-muted-foreground';
  return (
    <div className="bg-muted rounded-lg p-2.5 text-center">
      <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</p>
      <p className={`text-sm font-semibold mt-0.5 ${c}`}>{value}</p>
    </div>
  );
}

function ToggleRow({ id, icon, label, description, checked, onChange }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-start gap-3 min-w-0">
        <span className="mt-0.5 shrink-0">{icon}</span>
        <div className="min-w-0">
          <Label htmlFor={id} className="text-sm text-foreground cursor-pointer">{label}</Label>
          <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
        </div>
      </div>
      <Switch id={id} checked={checked} onCheckedChange={onChange} data-testid={`toggle-${id}`} />
    </div>
  );
}
